import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Add,
    ArrowBack,
    CallSplit,
    DeleteOutline,
    DragIndicator,
    EditOutlined,
    ExpandLess,
    ExpandMore,
    MergeType,
} from "@mui/icons-material";
import {
    DragDropContext,
    Draggable,
    type DraggableProvidedDragHandleProps,
    type DraggableProvidedDraggableProps,
    type DropResult,
} from "react-beautiful-dnd";
import { StrictModeDroppable } from "../../molecules/StrictModeDroppable";
import {
    fetchAnswerSetsPaginated,
    fetchAnswerSet,
    createAnswerSet,
    updateAnswerSet,
    deleteAnswerSet,
} from "../../../utils/api";
import type { AnswerSet, AnswerValue } from "../../../types/answerSet";
import RubricManagementList, { Column, type StatusFilter } from "../../molecules/RubricManagementList";
import useListState from "../../../hooks/useListState";
import { ConfirmationDialog } from "./ConfirmationDialog";

/**
 * Sanitize DnD provided props to prevent potential DOM-based XSS.
 * Only allows known-safe HTML attributes, data-* attributes, aria-* attributes,
 * event handlers, style, and ref — strips anything unexpected like dangerouslySetInnerHTML.
 */
const sanitizeDndProps = (
    props:
        | DraggableProvidedDragHandleProps
        | DraggableProvidedDraggableProps
        | Record<string, unknown>
        | null
        | undefined,
): Record<string, unknown> => {
    if (!props) return {};
    const safe: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(props)) {
        if (
            key === 'dangerouslySetInnerHTML' ||
            key === 'innerHTML' ||
            key === 'outerHTML'
        ) {
            continue;
        }
        safe[key] = value;
    }
    return safe;
};

type ViewMode = "list" | "detail";

type AnswerValueForm = (AnswerValue & { key: string; collapsed?: boolean });

interface AnswerSetFormState {
    name: string;
    description?: string;
    active: boolean;
    answers: AnswerValueForm[];
}

interface AnswerSetsPanelProps {
    onPathChange?: (path: string) => void;
}

const generateKey = () => `answer-${Math.random().toString(36).slice(2)}-${Date.now()}`;

const createEmptyAnswer = (): AnswerValueForm => ({
    key: generateKey(),
    name: "",
    tagLabel: "",
    description: "",
    legacyValues: [],
    active: true,
    selectable: true,
    collapsed: false,
});

const initialFormState: AnswerSetFormState = {
    name: "",
    description: "",
    active: true,
    answers: [createEmptyAnswer()],
};

const generateTagLabelFromName = (value: string) =>
    value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_");

const getLegacyIdentifier = (answer: AnswerValueForm) => answer.id?.toString() ?? answer.key;

// Column configuration for the answer sets list - single flowing layout
const answerSetColumns: Column<AnswerSet>[] = [
    {
        field: "name",
        label: "Name",
        sortable: true,
        width: "flex",
        render: (item) => {
            const exampleAnswer = item.answers?.[0]?.name;
            const descriptionWithExample = [
                item.description,
                exampleAnswer ? `Example: ${exampleAnswer}` : null,
            ]
                .filter(Boolean)
                .join(" · ");

            return (
                <Box>
                    <Typography variant="body1">
                        {item.name}
                    </Typography>
                    {item.currentUses && item.currentUses.length > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {item.currentUses.join(", ")}
                        </Typography>
                    )}
                    {descriptionWithExample && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {descriptionWithExample}
                        </Typography>
                    )}
                </Box>
            );
        },
    },
];

const AnswerSetsPanel: React.FC<AnswerSetsPanelProps> = ({ onPathChange }) => {
    const [viewMode, setViewMode] = useState<ViewMode>("list");
    const [answerSets, setAnswerSets] = useState<AnswerSet[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Use list state hook for URL-persisted pagination/search/sort
    const listState = useListState({
        defaultSortBy: "name",
        defaultSortDirection: "asc",
        defaultPageSize: 10,
        paramPrefix: "as", // "as" for answer sets to avoid conflicts
    });
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

    const [selectedAnswerSet, setSelectedAnswerSet] = useState<AnswerSet | null>(null);
    const [formValues, setFormValues] = useState<AnswerSetFormState>(initialFormState);
    const [formError, setFormError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState<AnswerSet | null>(null);
    const [splitDialog, setSplitDialog] = useState<{
        open: boolean;
        answerIndex: number | null;
        name: string;
        tagLabel: string;
        error: string | null;
    }>({
        open: false,
        answerIndex: null,
        name: "",
        tagLabel: "",
        error: null,
    });
    const [mergeDialog, setMergeDialog] = useState<{
        open: boolean;
        primaryIndex: number | null;
        secondaryIndex: number | null;
        name: string;
        tagLabel: string;
        error: string | null;
    }>({
        open: false,
        primaryIndex: null,
        secondaryIndex: null,
        name: "",
        tagLabel: "",
        error: null,
    });

    // Track newly added answer for auto-focus
    const newAnswerKeyRef = useRef<string | null>(null);

    const relatedAnswersMap = useMemo<Record<string, string[]>>(() => {
        if (!formValues.answers.length) {
            return {};
        }
        const identifierLookup = new Map<string, AnswerValueForm>();
        formValues.answers.forEach((answer) => {
            identifierLookup.set(answer.key, answer);
            if (answer.id != null) {
                identifierLookup.set(String(answer.id), answer);
            }
        });

        const map: Record<string, string[]> = {};
        formValues.answers.forEach((answer) => {
            const relatedNames = new Set<string>();
            (answer.legacyValues ?? []).forEach((legacyId) => {
                const match = legacyId ? identifierLookup.get(legacyId) : undefined;
                if (match && match.key !== answer.key && match.name) {
                    relatedNames.add(match.name);
                }
            });
            const answerId = answer.id ? String(answer.id) : null;
            if (answerId) {
                formValues.answers.forEach((other) => {
                    if (other.key === answer.key) {
                        return;
                    }
                    if ((other.legacyValues ?? []).some((value) => value === answerId) && other.name) {
                        relatedNames.add(other.name);
                    }
                });
            }
            map[answer.key] = Array.from(relatedNames);
        });
        return map;
    }, [formValues.answers]);

    const pathSuffix = useMemo(() => {
        if (viewMode === "list") {
            return "Answer Sets";
        }
        return selectedAnswerSet ? "Update Answer Set" : "Create Answer Set";
    }, [viewMode, selectedAnswerSet]);

    useEffect(() => {
        if (onPathChange) {
            onPathChange(pathSuffix);
        }
    }, [onPathChange, pathSuffix]);

    const loadAnswerSets = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchAnswerSetsPaginated({
                page: listState.page,
                size: listState.pageSize,
                sortBy: listState.sortBy,
                sortDirection: listState.sortDirection,
                search: listState.search || undefined,
                active: statusFilter === "active" ? true : statusFilter === "inactive" ? false : undefined,
            });
            setAnswerSets(response.content ?? []);
            setTotalElements(response.totalElements ?? 0);
        } catch (err) {
            console.error("Failed to fetch answer sets", err);
            setError(err instanceof Error ? err.message : "Unable to load answer sets");
        } finally {
            setLoading(false);
        }
    }, [listState.page, listState.pageSize, listState.sortBy, listState.sortDirection, listState.search, statusFilter]);

    useEffect(() => {
        loadAnswerSets();
    }, [loadAnswerSets]);

    const resetForm = () => {
        setFormValues({
            ...initialFormState,
            answers: [createEmptyAnswer()],
        });
        setFormError(null);
    };

    const handleSelectAnswerSet = async (answerSet: AnswerSet) => {
        setDetailLoading(true);
        setError(null);
        try {
            const detailed = await fetchAnswerSet(answerSet.id);
            setSelectedAnswerSet(detailed);
            const sortedAnswers = [...(detailed.answers ?? [])]
                .sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0));
            setFormValues({
                name: detailed.name,
                description: detailed.description ?? "",
                active: detailed.active ?? true,
                answers: sortedAnswers.map((answer, index) => ({
                    ...answer,
                    key: generateKey(),
                    legacyValues: answer.legacyValues ?? [],
                    collapsed: true,
                    sequenceOrder: answer.sequenceOrder ?? index + 1,
                })),
            });
            setViewMode("detail");
        } catch (err) {
            console.error("Failed to load answer set", err);
            setError(err instanceof Error ? err.message : "Unable to load answer set");
        } finally {
            setDetailLoading(false);
        }
    };

    const handleCreateNew = () => {
        setSelectedAnswerSet(null);
        resetForm();
        setViewMode("detail");
    };

    const handleBackToList = () => {
        setViewMode("list");
        setSelectedAnswerSet(null);
        resetForm();
    };

    const handleAnswerFieldChange = (index: number, field: keyof AnswerValue, value: unknown) => {
        setFormValues((prev) => {
            const answers = [...prev.answers];
            answers[index] = {
                ...answers[index],
                [field]: value,
            } as AnswerValueForm;
            return { ...prev, answers };
        });
    };

    const openSplitDialog = (answerIndex: number) => {
        const target = formValues.answers[answerIndex];
        if (!target) {
            return;
        }
        const suggestedName = target.name ? `${target.name} Copy` : "";
        const tagSeed = target.tagLabel
            ? `${target.tagLabel}_COPY`
            : generateTagLabelFromName((suggestedName || "new answer") + " copy");
        setSplitDialog({
            open: true,
            answerIndex,
            name: suggestedName,
            tagLabel: `${tagSeed || ""}`,
            error: null,
        });
    };

    const closeSplitDialog = () => {
        setSplitDialog({
            open: false,
            answerIndex: null,
            name: "",
            tagLabel: "",
            error: null,
        });
    };

    const handleConfirmSplit = () => {
        if (splitDialog.answerIndex == null) {
            closeSplitDialog();
            return;
        }
        const trimmedName = splitDialog.name.trim();
        const trimmedTag = splitDialog.tagLabel.trim();
        if (!trimmedName || !trimmedTag) {
            setSplitDialog((prev) => ({
                ...prev,
                error: "Name and tag label are required.",
            }));
            return;
        }
        setFormValues((prev) => {
            const answers = [...prev.answers];
            const source = answers[splitDialog.answerIndex!];
            if (!source) {
                return prev;
            }
            const updatedSource: AnswerValueForm = { ...source };
            const linkIdentifier = updatedSource.id ? String(updatedSource.id) : updatedSource.key;
            const sourceLegacy = new Set<string>(updatedSource.legacyValues ?? []);
            sourceLegacy.add(linkIdentifier);
            const newAnswer: AnswerValueForm = {
                ...createEmptyAnswer(),
                name: trimmedName,
                tagLabel: trimmedTag,
                description: updatedSource.description ?? "",
                legacyValues: Array.from(sourceLegacy),
                active: updatedSource.active ?? true,
                selectable: updatedSource.selectable ?? true,
                collapsed: false,
            };
            answers[splitDialog.answerIndex!] = updatedSource;
            answers.splice(splitDialog.answerIndex! + 1, 0, newAnswer);
            return {
                ...prev,
                answers,
            };
        });
        closeSplitDialog();
    };

    const openMergeDialog = (primaryIndex: number) => {
        const target = formValues.answers[primaryIndex];
        if (!target) {
            return;
        }
        const baseName = target.name?.trim() ?? "";
        const tagSeed = target.tagLabel
            ? `${target.tagLabel}_MERGED`
            : generateTagLabelFromName((baseName || "merged answer") + " merged");
        setMergeDialog({
            open: true,
            primaryIndex,
            secondaryIndex: null,
            name: baseName ? `${baseName} Merge` : "",
            tagLabel: `${tagSeed}`,
            error: null,
        });
    };

    const closeMergeDialog = () => {
        setMergeDialog({
            open: false,
            primaryIndex: null,
            secondaryIndex: null,
            name: "",
            tagLabel: "",
            error: null,
        });
    };

    const handleConfirmMerge = () => {
        const { primaryIndex, secondaryIndex } = mergeDialog;
        if (primaryIndex == null || secondaryIndex == null) {
            setMergeDialog((prev) => ({
                ...prev,
                error: "Select two answers to merge.",
            }));
            return;
        }
        const trimmedName = mergeDialog.name.trim();
        const trimmedTag = mergeDialog.tagLabel.trim();
        if (!trimmedName || !trimmedTag) {
            setMergeDialog((prev) => ({
                ...prev,
                error: "Name and tag label are required.",
            }));
            return;
        }
        if (primaryIndex === secondaryIndex) {
            setMergeDialog((prev) => ({
                ...prev,
                error: "Choose a different answer to merge with.",
            }));
            return;
        }
        setFormValues((prev) => {
            const answers = [...prev.answers];
            const first = answers[primaryIndex];
            const second = answers[secondaryIndex];
            if (!first || !second) {
                return prev;
            }
            const combinedLegacy = new Set<string>([
                ...(first.legacyValues ?? []),
                ...(second.legacyValues ?? []),
                getLegacyIdentifier(first),
                getLegacyIdentifier(second),
            ]);
            const newAnswer: AnswerValueForm = {
                ...createEmptyAnswer(),
                name: trimmedName,
                tagLabel: trimmedTag,
                description: first.description || second.description || "",
                legacyValues: Array.from(combinedLegacy),
                active: true,
                selectable: true,
                collapsed: false,
            };
            answers.push(newAnswer);
            return {
                ...prev,
                answers,
            };
        });
        closeMergeDialog();
    };

    const handleAddAnswer = () => {
        const newKey = generateKey();
        newAnswerKeyRef.current = newKey;
        setFormValues((prev) => {
            const newAnswer = {
                ...createEmptyAnswer(),
                key: newKey,
                sequenceOrder: prev.answers.length + 1,
                collapsed: false,
            };
            return {
                ...prev,
                answers: [...prev.answers, newAnswer],
            };
        });
    };

    const handleRemoveAnswer = (index: number) => {
        setFormValues((prev) => {
            const answers = prev.answers.filter((_, idx) => idx !== index);
            // Recalculate sequenceOrder after removal
            const reorderedAnswers = answers.map((answer, idx) => ({
                ...answer,
                sequenceOrder: idx + 1,
            }));
            return { ...prev, answers: reorderedAnswers.length ? reorderedAnswers : [createEmptyAnswer()] };
        });
    };

    const handleAnswerDragEnd = useCallback((result: DropResult) => {
        if (!result.destination || result.destination.index === result.source.index) {
            return;
        }
        setFormValues((prev) => {
            const updated = [...prev.answers];
            const [moved] = updated.splice(result.source.index, 1);
            if (!moved) {
                return prev;
            }
            updated.splice(result.destination!.index, 0, moved);
            // Recalculate sequenceOrder for all items
            const reordered = updated.map((answer, idx) => ({
                ...answer,
                sequenceOrder: idx + 1,
            }));
            return { ...prev, answers: reordered };
        });
    }, []);

    const handleToggleAnswerCollapse = (key: string) => {
        setFormValues((prev) => ({
            ...prev,
            answers: prev.answers.map((answer) =>
                answer.key === key ? { ...answer, collapsed: !answer.collapsed } : answer
            ),
        }));
    };

    const handleCollapseAll = () => {
        setFormValues((prev) => ({
            ...prev,
            answers: prev.answers.map((answer) => ({ ...answer, collapsed: true })),
        }));
    };

    const handleExpandAll = () => {
        setFormValues((prev) => ({
            ...prev,
            answers: prev.answers.map((answer) => ({ ...answer, collapsed: false })),
        }));
    };

    const handleSave = async () => {
        if (!formValues.name.trim()) {
            setFormError("Name is required");
            return;
        }
        const invalidAnswer = formValues.answers.find(
            (answer) => !answer.name.trim() || !answer.tagLabel.trim()
        );
        if (invalidAnswer) {
            setFormError("Each answer requires a Name and Tag Label");
            return;
        }

        setFormError(null);
        setSaving(true);
        try {
            const payloadAnswers = formValues.answers.map(({ key, collapsed, ...answer }, index) => ({
                ...answer,
                legacyValues: answer.legacyValues ?? [],
                sequenceOrder: answer.sequenceOrder ?? index + 1,
            }));

            if (selectedAnswerSet) {
                await updateAnswerSet(selectedAnswerSet.id, {
                    name: formValues.name.trim(),
                    description: formValues.description?.trim(),
                    active: formValues.active,
                    answers: payloadAnswers,
                });
            } else {
                await createAnswerSet({
                    name: formValues.name.trim(),
                    description: formValues.description?.trim(),
                    answers: payloadAnswers,
                });
            }
            await loadAnswerSets();
            handleBackToList();
        } catch (err) {
            console.error("Unable to save answer set", err);
            setFormError(err instanceof Error ? err.message : "Unable to save answer set");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteAnswerSet = (answerSet: AnswerSet) => {
        setDeleteConfirmation(answerSet);
    };

    const confirmDeleteAnswerSet = async () => {
        if (!deleteConfirmation?.id) {
            setDeleteConfirmation(null);
            return;
        }
        const answerSet = deleteConfirmation;
        setDeleteConfirmation(null);
        try {
            await deleteAnswerSet(answerSet.id);
            await loadAnswerSets();
        } catch (err) {
            console.error("Failed to delete answer set", err);
            setError(err instanceof Error ? err.message : "Unable to delete answer set");
        }
    };

    const handleToggleActive = async (answerSet: AnswerSet, active: boolean) => {
        if (!answerSet.id) {
            return;
        }
        try {
            // Ensure we include the full answers list so a PUT doesn't drop existing answers.
            const detailed = await fetchAnswerSet(answerSet.id);
            if (!detailed.answers) {
                throw new Error("Unable to load answer values for this answer set.");
            }
            await updateAnswerSet(answerSet.id, {
                name: detailed.name,
                description: detailed.description,
                active,
                answers: detailed.answers,
            });
            await loadAnswerSets();
        } catch (err) {
            console.error("Failed to toggle answer set active status", err);
            setError(err instanceof Error ? err.message : "Unable to update answer set");
        }
    };

    const renderActions = (answerSet: AnswerSet) => (
        <>
            <Tooltip title="Edit">
                <IconButton
                    onClick={(event) => {
                        event.stopPropagation();
                        handleSelectAnswerSet(answerSet);
                    }}
                    sx={{ color: "#7498A6" }}
                >
                    <EditOutlined fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton
                    aria-label={`Delete answer set ${answerSet.name}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteAnswerSet(answerSet);
                    }}
                    sx={{ color: "#7498A6" }}
                >
                    <DeleteOutline fontSize="small" />
                </IconButton>
            </Tooltip>
        </>
    );

    const renderAnswerCard = (
        answer: AnswerValueForm,
        index: number,
        dragHandleProps?: DraggableProvidedDragHandleProps | null,
    ) => {
        const isCollapsed = Boolean(answer.collapsed);
        const rank = index + 1;
        return (
            <Paper
                variant="outlined"
                sx={{
                    backgroundColor: "#F6FBFF",
                    borderColor: "#BBDCFB",
                    borderRadius: "3px",
                    p: 1.5,
                }}
            >
                <Stack spacing={1} width="100%">
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Drag handle */}
                        <Box
                            component="span"
                            {...sanitizeDndProps(dragHandleProps)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "grab",
                                color: "text.secondary",
                                "&:active": { cursor: "grabbing" },
                            }}
                        >
                            <DragIndicator fontSize="small" />
                        </Box>
                        {/* Rank number */}
                        <Typography
                            variant="body2"
                            sx={{
                                minWidth: 24,
                                textAlign: "center",
                                fontWeight: 600,
                                color: "#7498A6",
                            }}
                        >
                            {rank}
                        </Typography>
                        <Switch
                            size="small"
                            checked={Boolean(answer.active)}
                            onChange={(event) =>
                                handleAnswerFieldChange(index, "active", event.target.checked)
                            }
                            sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": { color: "#F28D7A" },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#F9C6BA" },
                            }}
                        />
                        <Typography variant="body1" noWrap sx={{ flexGrow: 1 }}>
                            {answer.name || "Untitled Answer"}
                        </Typography>
                        <Tooltip title={isCollapsed ? "Expand" : "Collapse"}>
                            <IconButton
                                size="small"
                                onClick={() => handleToggleAnswerCollapse(answer.key)}
                                aria-label={`${isCollapsed ? "Expand" : "Collapse"} answer`}
                                aria-expanded={!isCollapsed}
                                sx={{ color: "#7498A6" }}
                            >
                                {isCollapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete answer">
                            <IconButton
                                size="small"
                                onClick={() => handleRemoveAnswer(index)}
                                sx={{ color: "#7498A6" }}
                            >
                                <DeleteOutline fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                        <Stack spacing={2} mt={1} ml={6}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                <TextField
                                    label="Name"
                                    value={answer.name}
                                    onChange={(event) =>
                                        handleAnswerFieldChange(index, "name", event.target.value)
                                    }
                                    required
                                    fullWidth
                                    size="small"
                                    autoFocus={newAnswerKeyRef.current === answer.key}
                                    onFocus={() => {
                                        // Clear the ref after focus to prevent re-focusing on re-renders
                                        if (newAnswerKeyRef.current === answer.key) {
                                            newAnswerKeyRef.current = null;
                                        }
                                    }}
                                />
                                <TextField
                                    label="Tag Label"
                                    value={answer.tagLabel}
                                    onChange={(event) =>
                                        handleAnswerFieldChange(index, "tagLabel", event.target.value)
                                    }
                                    required
                                    fullWidth
                                    size="small"
                                />
                            </Stack>
                            <TextField
                                label="Description"
                                value={answer.description ?? ""}
                                onChange={(event) =>
                                    handleAnswerFieldChange(index, "description", event.target.value)
                                }
                                fullWidth
                                multiline
                                minRows={2}
                                size="small"
                            />
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<CallSplit />}
                                    onClick={() => openSplitDialog(index)}
                                    disabled={!Boolean(answer.name?.trim() && answer.tagLabel?.trim())}
                                >
                                    Split Answer
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<MergeType />}
                                    onClick={() => openMergeDialog(index)}
                                    disabled={formValues.answers.length < 2}
                                >
                                    Merge Answer
                                </Button>
                            </Stack>
                            {relatedAnswersMap[answer.key]?.length ? (
                                <Typography variant="body2" color="text.secondary">
                                    Related answers: {relatedAnswersMap[answer.key].join(", ")}
                                </Typography>
                            ) : null}
                        </Stack>
                    </Collapse>
                </Stack>
            </Paper>
        );
    };

    const renderListView = () => (
        <RubricManagementList<AnswerSet>
            items={answerSets}
            totalCount={totalElements}
            loading={loading}
            error={error}
            listState={listState}
            columns={answerSetColumns}
            getItemKey={(item) => item.id}
            onItemClick={handleSelectAnswerSet}
            onAddClick={handleCreateNew}
            searchPlaceholder="Search answer sets"
            addButtonLabel="Add Answers"
            itemLabel="answer sets"
            emptyMessage="No answer sets found."
            renderActions={renderActions}
            statusFilter={statusFilter}
            onStatusFilterChange={(value: StatusFilter) => { setStatusFilter(value); listState.setPage(0); }}
            onToggleActive={handleToggleActive}
            isItemActive={(item) => Boolean(item.active)}
        />
    );

    const renderDetailView = () => (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBackToList}>
                    Back to Answer Sets
                </Button>
                <Stack direction="row" spacing={1}>
                    <Button onClick={handleBackToList}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving || detailLoading}>
                        {saving ? "Saving..." : selectedAnswerSet ? "Update" : "Create"}
                    </Button>
                </Stack>
            </Stack>
            {detailLoading ? (
                <Stack alignItems="center" py={6}>
                    <CircularProgress />
                </Stack>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {formError && <Alert severity="error">{formError}</Alert>}
                    <TextField
                        label="Name"
                        value={formValues.name}
                        onChange={(event) =>
                            setFormValues((prev) => ({ ...prev, name: event.target.value }))
                        }
                        required
                        fullWidth
                    />
                    <TextField
                        label="Description"
                        value={formValues.description ?? ""}
                        onChange={(event) =>
                            setFormValues((prev) => ({ ...prev, description: event.target.value }))
                        }
                        fullWidth
                        multiline
                        minRows={2}
                    />
                    {selectedAnswerSet?.currentUses && selectedAnswerSet.currentUses.length > 0 && (
                        <Alert severity="info">
                            Current Uses: {selectedAnswerSet.currentUses.join(", ")}
                        </Alert>
                    )}
                    <Divider />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1">Values</Typography>
                        <Stack direction="row" spacing={1}>
                            <Button
                                size="small"
                                onClick={handleExpandAll}
                                disabled={formValues.answers.length === 0}
                            >
                                Expand All
                            </Button>
                            <Button
                                size="small"
                                onClick={handleCollapseAll}
                                disabled={formValues.answers.length === 0}
                            >
                                Collapse All
                            </Button>
                        </Stack>
                    </Stack>
                    {/* Draggable answers list */}
                    <DragDropContext onDragEnd={handleAnswerDragEnd}>
                        <StrictModeDroppable droppableId="answers-list">
                            {(droppableProvided) => (
                                <Stack
                                    spacing={1.5}
                                    ref={droppableProvided.innerRef}
                                    {...droppableProvided.droppableProps}
                                >
                                    {formValues.answers.map((answer, index) => (
                                        <Draggable
                                            key={answer.key}
                                            draggableId={answer.key}
                                            index={index}
                                        >
                                            {(draggableProvided, snapshot) => (
                                                <Box
                                                    ref={draggableProvided.innerRef}
                                                    {...sanitizeDndProps(draggableProvided.draggableProps)}
                                                    sx={{
                                                        opacity: snapshot.isDragging ? 0.9 : 1,
                                                        boxShadow: snapshot.isDragging ? 4 : 0,
                                                    }}
                                                >
                                                    {renderAnswerCard(answer, index, draggableProvided.dragHandleProps)}
                                                </Box>
                                            )}
                                        </Draggable>
                                    ))}
                                    {droppableProvided.placeholder as any}
                                </Stack>
                            )}
                        </StrictModeDroppable>
                    </DragDropContext>
                    {/* Add Answer button at the bottom for natural tab flow */}
                    <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={handleAddAnswer}
                        fullWidth
                        sx={{
                            borderStyle: "dashed",
                            borderColor: "#BBDCFB",
                            color: "#7498A6",
                            "&:hover": {
                                borderColor: "#186AE1",
                                backgroundColor: "rgba(24, 106, 225, 0.04)",
                            },
                        }}
                    >
                        Add Answer
                    </Button>
                </Box>
            )}
        </Box>
    );

    const renderSplitDialog = () => (
        <Dialog open={splitDialog.open} onClose={closeSplitDialog} fullWidth maxWidth="sm">
            <DialogTitle>Split Answer</DialogTitle>
            <DialogContent>
                <Stack spacing={2} mt={1}>
                    <TextField
                        label="New Answer Name"
                        value={splitDialog.name}
                        onChange={(event) => setSplitDialog((prev) => ({ ...prev, name: event.target.value }))}
                        autoFocus
                        required
                        fullWidth
                    />
                    <TextField
                        label="New Tag Label"
                        value={splitDialog.tagLabel}
                        onChange={(event) => setSplitDialog((prev) => ({ ...prev, tagLabel: event.target.value }))}
                        required
                        fullWidth
                    />
                    <Typography variant="body2" color="text.secondary">
                        The new answer will inherit the original legacy values so both answers remain linked.
                    </Typography>
                    {splitDialog.error && (
                        <Alert severity="error">
                            {splitDialog.error}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={closeSplitDialog}>Cancel</Button>
                <Button variant="contained" onClick={handleConfirmSplit}>
                    Split
                </Button>
            </DialogActions>
        </Dialog>
    );

    const renderMergeDialog = () => {
        const availableTargets = formValues.answers
            .map((answer, idx) => ({
                label: answer.name?.trim() || `Answer ${idx + 1}`,
                index: idx,
            }))
            .filter((option) => option.index !== mergeDialog.primaryIndex);

        return (
            <Dialog open={mergeDialog.open} onClose={closeMergeDialog} fullWidth maxWidth="sm">
                <DialogTitle>Merge Answers</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <FormControl fullWidth>
                            <InputLabel id="merge-answer-select-label">Answer to Merge With</InputLabel>
                            <Select
                                labelId="merge-answer-select-label"
                                label="Answer to Merge With"
                                value={
                                    mergeDialog.secondaryIndex !== null
                                        ? String(mergeDialog.secondaryIndex)
                                        : ""
                                }
                                onChange={(event) =>
                                    setMergeDialog((prev) => ({
                                        ...prev,
                                        secondaryIndex: event.target.value ? Number(event.target.value) : null,
                                    }))
                                }
                            >
                                {availableTargets.length === 0 ? (
                                    <MenuItem value="" disabled>
                                        No other answers available
                                    </MenuItem>
                                ) : (
                                    availableTargets.map((option) => (
                                        <MenuItem key={option.index} value={String(option.index)}>
                                            {option.label}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Merged Answer Name"
                            value={mergeDialog.name}
                            onChange={(event) =>
                                setMergeDialog((prev) => ({ ...prev, name: event.target.value }))
                            }
                            required
                            fullWidth
                        />
                        <TextField
                            label="Merged Tag Label"
                            value={mergeDialog.tagLabel}
                            onChange={(event) =>
                                setMergeDialog((prev) => ({ ...prev, tagLabel: event.target.value }))
                            }
                            required
                            fullWidth
                        />
                        <Typography variant="body2" color="text.secondary">
                            A new answer will be created with the combined legacy values of both answers.
                        </Typography>
                        {mergeDialog.error && (
                            <Alert severity="error">
                                {mergeDialog.error}
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeMergeDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleConfirmMerge} disabled={availableTargets.length === 0}>
                        Merge
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    return (
        <>
            {viewMode === "list" ? renderListView() : renderDetailView()}
            <ConfirmationDialog
                open={Boolean(deleteConfirmation)}
                title="Delete answer set?"
                description={
                    deleteConfirmation
                        ? `Delete answer set "${deleteConfirmation.name}"?`
                        : ""
                }
                confirmLabel="Delete"
                confirmColor="error"
                titleId="delete-answer-set-title"
                descriptionId="delete-answer-set-description"
                onCancel={() => setDeleteConfirmation(null)}
                onConfirm={confirmDeleteAnswerSet}
            />
            {renderSplitDialog()}
            {renderMergeDialog()}
        </>
    );
};

export default AnswerSetsPanel;
